import React, { useEffect, useState } from "react";
import ModalShell from "./ModalShell";
import NumericPopoverPicker from "../NumericPopoverPicker";
import {
  GAMES_CATALOG,
  GameDefinition,
  GameOption,
  defaultOptionsFor,
} from "../gamesCatalog";
import { ActiveGame } from "../activeRound";
import { Player } from "../types";
import { shuffleRoundRobinPairings } from "../runners/hollywood";

// Default range used when a number-type GameOption doesn't pin both
// ends of its range. The wheel needs a finite item list, so we cap
// uncapped options at 100. Bet/point fields in the catalog all sit
// well below this; if a future option needs a higher ceiling, set
// `max` on the option and the wheel uses that instead.
const NUMBER_DEFAULT_MAX = 100;

interface Props {
  games: ActiveGame[];
  // Round's current player list. Used for the player-count gate
  // (games with min/max constraints) and for the handicap-aware
  // Net/Gross default — when any player has a registered handicap,
  // scoringMode initialises to "net" the first time a game with
  // that option is enabled; otherwise "gross".
  players: Player[];
  onChange: (next: ActiveGame[]) => void;
  onClose: () => void;
}

// Override `defaultOptionsFor` after the catalog defaults have been
// stamped. Only consulted when a game is first enabled — the user's
// subsequent edits persist through the round. Two adjustments today:
//   • scoringMode flips to "net" when any player carries a registered
//     handicap, "gross" otherwise (skipped on games with no handicap
//     concept).
//   • Hollywood's three round-robin pairings get randomised — the
//     game's whole identity is the rotation, so shipping the same
//     defaults to every round would make it predictable. Users can
//     still re-shuffle from the leaderboard.
function defaultsWithHandicapAwareScoring(
  gameId: string,
  hasAnyHandicap: boolean,
): Record<string, boolean | number | string> {
  const out = defaultOptionsFor(gameId);
  const def = GAMES_CATALOG.find((g) => g.id === gameId);
  if (!def) return out;

  const scoringOpt = def.options.find((o) => o.key === "scoringMode");
  if (scoringOpt) {
    const wantNet = hasAnyHandicap;
    const choices = (scoringOpt.choices ?? []).map((c) => c.value);
    if (wantNet && choices.includes("net")) out.scoringMode = "net";
    else if (!wantNet && choices.includes("gross")) out.scoringMode = "gross";
  }

  if (gameId === "hollywood") {
    const [p1, p2, p3] = shuffleRoundRobinPairings();
    out.pairing1 = p1;
    out.pairing2 = p2;
    out.pairing3 = p3;
  }

  return out;
}

// Whether a game's player-count gate is satisfied. `minPlayers` and
// `maxPlayers` are both optional — missing ends are treated as
// open-ended. A round with 0 players (shouldn't happen in practice)
// is permissive so the modal still renders before any pairing.
function fitsPlayerCount(def: GameDefinition, count: number): boolean {
  if (count <= 0) return true;
  if (def.minPlayers != null && count < def.minPlayers) return false;
  if (def.maxPlayers != null && count > def.maxPlayers) return false;
  return true;
}

// Games that share the "Bet per Player / Bet per Point" radio grid
// in place of the bare Overall bet input. Filtered out of the default
// option loop and routed through StablefordBetGrid instead.
const HAS_BET_MODE_GRID = new Set([
  "stableford",
  "strokePlay",
  "putting",
  "hiLo",
  "acesAndDeuces",
  "hollywood",
]);

// Keys owned by the bet-mode grid for games in HAS_BET_MODE_GRID —
// the default render loop skips these so they don't double up.
const BET_GRID_KEYS = new Set([
  "overallBet",
  "betPerPoint",
  "useBetPerPoint",
  "frontNineBet",
  "backNineBet",
]);

// Wolf renders its own side-by-side block (WolfBetGrid) for these three
// options; the default primary/advanced loops skip them to avoid doubling.
const WOLF_GRID_KEYS = new Set([
  "betPerPlayer",
  "blindEnabled",
  "blindMultiplier",
]);

// Hollywood's shotgun-start controls render as one inline row (checkbox +
// hole selector on the same line) instead of two stacked option fields, so
// both keys are pulled out of the default advanced-options loop.
const HOLLYWOOD_START_KEYS = new Set(["changeStartHole", "startHole"]);
const HOLE_VALUES = Array.from({ length: 18 }, (_, i) => i + 1);

// Resolve the rendered label for an option — usually `option.label`,
// but a small handful of options swap their label based on other
// option values. Today this is the bet-mode-grid games' overallBet,
// which reads as "Bet per Player" by default and switches to
// "Overall bet" when Nassau is ticked.
function resolveOptionLabel(
  gameId: string,
  option: GameOption,
  optionValues: Record<string, boolean | number | string>,
): string {
  if (HAS_BET_MODE_GRID.has(gameId) && option.key === "overallBet") {
    return optionValues.nassau === true ? "Overall bet" : "Bet per Player";
  }
  return option.label;
}

// Human-readable "needs N players" string for the gated-row notice.
// Handles fixed counts (min === max), open-ended floors / ceilings,
// and bounded ranges. Returns null when the game has no player gate.
function playerGateLabel(def: GameDefinition): string | null {
  const { minPlayers, maxPlayers } = def;
  if (minPlayers == null && maxPlayers == null) return null;
  if (
    minPlayers != null &&
    maxPlayers != null &&
    minPlayers === maxPlayers
  ) {
    return `Requires ${minPlayers} players`;
  }
  if (minPlayers != null && maxPlayers != null) {
    return `Requires ${minPlayers}–${maxPlayers} players`;
  }
  if (minPlayers != null) return `Requires ${minPlayers}+ players`;
  return `Max ${maxPlayers} players`;
}

// Round-level games configuration. Designed for fast scanning:
//   • Default row = checkbox + name + info icon. No options visible.
//   • Tap info icon → inline description toggles.
//   • Tick the checkbox → "primary" options appear inline (the bet
//     amounts / counts the user typically tweaks first).
//   • "Options ▾" → expands the rest (rule variants, gates, sub-games).
//
// Primary vs advanced is presentation-only — the gamesCatalog stays
// untouched. The classification rule lives entirely in this file:
// number-type options surface as primary, everything else (boolean /
// select) collapses under the advanced disclosure. Two escape hatches
// from the default rule: an option flagged `advanced` is forced under
// the disclosure, and any option with a `dependsOn` is also collapsed
// (it only renders when the parent option is in the right state, so
// surfacing it inline would just leave a flickering field).
function isPrimaryOption(opt: GameOption): boolean {
  if (opt.advanced) return false;
  if (opt.dependsOn) return false;
  return opt.type === "number";
}

// Whether an option's `dependsOn` predicate is satisfied by the
// current option values. Options with no `dependsOn` are always shown.
// `value` can be a single match or a list — an array predicate is
// satisfied when the parent option's current value is any element of
// the list.
function isOptionVisible(
  opt: GameOption,
  options: Record<string, boolean | number | string>,
): boolean {
  if (!opt.dependsOn) return true;
  const actual = options[opt.dependsOn.key];
  const expected = opt.dependsOn.value;
  if (Array.isArray(expected)) return expected.includes(actual);
  return actual === expected;
}

export default function GamesModal({
  games,
  players,
  onChange,
  onClose,
}: Props) {
  const playerCount = players.length;
  // Used by setEnabled to pick the right Net/Gross default the first
  // time a game is toggled on — net when any player carries a registered
  // handicap, gross otherwise.
  const hasAnyHandicap = players.some((p) => p.handicap != null);
  // Two independent disclosures per game — info description and the
  // advanced-options panel. Each tracks the currently open game id (or
  // null), so opening one collapses the previous.
  const [infoOpen, setInfoOpen] = useState<string | null>(null);
  const [advancedOpen, setAdvancedOpen] = useState<string | null>(null);

  const findActive = (id: string): ActiveGame | undefined =>
    games.find((g) => g.id === id);

  // Mandatory games (Stroke Play) auto-enable on open and stay on.
  // Idempotent — only fires onChange when something is actually
  // missing or off. Covers rounds saved before mandatory games existed
  // in the catalog, and acts as a safety net even though new rounds
  // already seed mandatory games at creation time.
  useEffect(() => {
    const mandatory = GAMES_CATALOG.filter((g) => g.mandatory);
    if (mandatory.length === 0) return;

    let next = games;
    let changed = false;
    for (const g of mandatory) {
      const existing = next.find((x) => x.id === g.id);
      if (!existing) {
        next = [
          ...next,
          {
            id: g.id,
            enabled: true,
            options: defaultsWithHandicapAwareScoring(g.id, hasAnyHandicap),
          },
        ];
        changed = true;
      } else if (!existing.enabled) {
        next = next.map((x) =>
          x.id === g.id ? { ...x, enabled: true } : x,
        );
        changed = true;
      }
    }
    if (changed) onChange(next);
    // games / onChange identities are stable enough for our purposes —
    // CaddieGames hands us a fresh array only when something actually
    // changes upstream.
  }, [games, onChange]);

  const setEnabled = (id: string, enabled: boolean) => {
    const def = GAMES_CATALOG.find((g) => g.id === id);
    if (def?.mandatory) return; // mandatory games can't be toggled off

    const existing = findActive(id);
    if (enabled) {
      if (existing) {
        onChange(
          games.map((g) => (g.id === id ? { ...g, enabled: true } : g)),
        );
      } else {
        onChange([
          ...games,
          {
            id,
            enabled: true,
            options: defaultsWithHandicapAwareScoring(id, hasAnyHandicap),
          },
        ]);
      }
    } else {
      // Disable in place; preserve user's option overrides for next
      // re-enable. Also collapse advanced if it was the open one.
      if (existing) {
        onChange(
          games.map((g) => (g.id === id ? { ...g, enabled: false } : g)),
        );
      }
      if (advancedOpen === id) setAdvancedOpen(null);
    }
  };

  const setOption = (
    id: string,
    key: string,
    value: boolean | number | string,
  ) => {
    onChange(
      games.map((g) => {
        if (g.id !== id) return g;
        const nextOptions = { ...g.options, [key]: value };

        // Nassau cascade for bet-mode-grid games (Stableford + Stroke
        // Play). Editing the Overall bet (or flipping Nassau on for the
        // first time) auto-populates the front-9 and back-9 segment
        // bets to match — convenient single-tap setup for the common
        // "$5 across all three" case. Editing Front 9 or Back 9
        // directly never touches Overall, so a player can still raise
        // just one segment after the initial cascade. Other games
        // explicitly opted out of this linkage.
        if (HAS_BET_MODE_GRID.has(id)) {
          const nassauOn =
            key === "nassau"
              ? value === true
              : nextOptions.nassau === true;
          if (nassauOn && key === "overallBet") {
            nextOptions.frontNineBet = value;
            nextOptions.backNineBet = value;
          } else if (key === "nassau" && value === true) {
            const overall = nextOptions.overallBet;
            nextOptions.frontNineBet = overall;
            nextOptions.backNineBet = overall;
          }
        }

        // Match Play: Team (2-v-2) and Individual matches (two 1-v-1) are
        // mutually exclusive shapes of the same fourball. Turning one on
        // clears the other so the runner never sees both set.
        if (id === "matchPlay" && value === true) {
          if (key === "team") nextOptions.dualMatch = false;
          else if (key === "dualMatch") nextOptions.team = false;
        }

        return { ...g, options: nextOptions };
      }),
    );
  };

  return (
    <ModalShell
      title="Games"
      onClose={onClose}
      footer={
        <button
          type="button"
          className="cg-btn cg-btn-primary"
          onClick={onClose}
        >
          Done
        </button>
      }
    >
      <ul className="cg-games-list">
        {GAMES_CATALOG.map((game) => {
          const fits = fitsPlayerCount(game, playerCount);
          const gateLabel = fits ? null : playerGateLabel(game);
          return (
            <GameRow
              key={game.id}
              game={game}
              active={findActive(game.id)}
              fits={fits}
              playerCount={playerCount}
              gateLabel={gateLabel}
              infoOpen={infoOpen === game.id}
              advancedOpen={advancedOpen === game.id}
              onToggleEnabled={(enabled) => setEnabled(game.id, enabled)}
              onToggleInfo={() =>
                setInfoOpen((cur) => (cur === game.id ? null : game.id))
              }
              onToggleAdvanced={() =>
                setAdvancedOpen((cur) => (cur === game.id ? null : game.id))
              }
              onOptionChange={(key, value) =>
                setOption(game.id, key, value)
              }
            />
          );
        })}
      </ul>
    </ModalShell>
  );
}

// ─── Single game row ───────────────────────────────────────────────────────

interface RowProps {
  game: GameDefinition;
  active?: ActiveGame;
  // Whether the round's player count satisfies the game's player gate.
  // false → checkbox disabled, options collapsed, gate notice shown.
  fits: boolean;
  // Number of players on the card — drives per-option minPlayers gating
  // (e.g. the 2-v-2 Team controls hide below a foursome).
  playerCount: number;
  // Human-readable reason the row is gated ("Requires 3 players").
  // Only set when fits === false; null otherwise.
  gateLabel: string | null;
  infoOpen: boolean;
  advancedOpen: boolean;
  onToggleEnabled: (enabled: boolean) => void;
  onToggleInfo: () => void;
  onToggleAdvanced: () => void;
  onOptionChange: (key: string, value: boolean | number | string) => void;
}

function GameRow({
  game,
  active,
  fits,
  playerCount,
  gateLabel,
  infoOpen,
  advancedOpen,
  onToggleEnabled,
  onToggleInfo,
  onToggleAdvanced,
  onOptionChange,
}: RowProps) {
  // Mandatory games render as enabled even on the first paint before
  // the auto-seed effect has run, with default options as a stand-in
  // so the row never appears "off". Edits made on this first paint
  // are still safe — they go through onOptionChange, and by the time
  // the user actually tweaks something the seed has run.
  const isMandatory = !!game.mandatory;
  // A row that doesn't fit the player count is force-disabled in the
  // UI even if the stored ActiveGame still has enabled: true. We
  // intentionally don't mutate the underlying state on render — the
  // user might add a 3rd player later and want Nines to come back on.
  const enabled = !fits ? false : isMandatory ? true : !!active?.enabled;
  const optionValues = active?.options ?? defaultOptionsFor(game.id);
  // Options hidden purely because the card is below their minPlayers
  // (e.g. the 2-v-2 Team checkbox under a foursome). Their dependent
  // fields (team setup / team scoring) must drop too, even if a stale
  // `team: true` value lingers from when the card had 4 players.
  const playerHiddenKeys = new Set(
    game.options
      .filter((o) => o.minPlayers != null && playerCount < o.minPlayers)
      .map((o) => o.key),
  );
  const visibleOpts = game.options.filter((o) => {
    if (o.minPlayers != null && playerCount < o.minPlayers) return false;
    if (o.dependsOn && playerHiddenKeys.has(o.dependsOn.key)) return false;
    return isOptionVisible(o, optionValues);
  });
  const primaryOpts = visibleOpts.filter(isPrimaryOption);
  const advancedOpts = visibleOpts.filter((o) => !isPrimaryOption(o));
  const hasAdvanced = advancedOpts.length > 0;

  return (
    <li
      className={`cg-games-row${enabled ? " cg-games-row-on" : ""}${
        fits ? "" : " cg-games-row-blocked"
      }`}
    >
      <div className="cg-games-row-head">
        <label className="cg-games-toggle-label">
          <input
            type="checkbox"
            className="cg-games-toggle"
            checked={enabled}
            disabled={isMandatory || !fits}
            onChange={(e) => onToggleEnabled(e.target.checked)}
          />
          <span className="cg-games-name">{game.name}</span>
        </label>
        {/* Gate tag sits as a row-head sibling (not inside the name label) so
            margin-left:auto floats it to the right edge — uniform across all
            blocked rows regardless of game-name length. */}
        {!fits && gateLabel && (
          <span className="cg-games-gate-tag" aria-label={gateLabel}>
            {gateLabel}
          </span>
        )}
        {/* Options + Info buttons clump at the right of the row. The
            options gear absorbs the free space via margin-left: auto
            (in CSS) so it pulls both icons together to the right edge.
            Gear only renders when the game is on AND there's something
            to configure. */}
        {enabled && hasAdvanced && (
          <button
            type="button"
            className={`cg-games-options-btn${
              advancedOpen ? " cg-games-options-on" : ""
            }`}
            onClick={onToggleAdvanced}
            aria-label={`Options for ${game.name}`}
            aria-expanded={advancedOpen}
            title="Options"
          >
            {"⚙"}
          </button>
        )}
        <button
          type="button"
          className={`cg-games-info-btn${infoOpen ? " cg-games-info-on" : ""}`}
          onClick={onToggleInfo}
          aria-label={`About ${game.name}`}
          aria-expanded={infoOpen}
          title="Rules"
        >
          i
        </button>
      </div>

      {infoOpen && <p className="cg-games-desc">{game.description}</p>}

      {/* Primary inline options surface as soon as the game is on. No
          disclosure — these are the "what's the bet?" knobs the user
          expects to see at a glance. */}
      {enabled && primaryOpts.length > 0 && (
        <div className={`cg-games-primary cg-games-primary-${game.id}`}>
          {primaryOpts.map((opt) => (
            <GameOptionField
              key={opt.key}
              option={opt}
              label={resolveOptionLabel(game.id, opt, optionValues)}
              value={optionValues[opt.key]}
              onChange={(v) => onOptionChange(opt.key, v)}
            />
          ))}
        </div>
      )}

      {/* Advanced panel — driven by the inline toggle above. */}
      {enabled && hasAdvanced && advancedOpen && (
        <div className="cg-games-advanced">
          {/* Wolf — Bet per Player + Blind Wolf Multiplier box, side by side.
              Lives under the gear like every other game's bet inputs. Owns
              betPerPlayer / blindEnabled / blindMultiplier; those keys are
              filtered out of the loop below. */}
          {game.id === "wolf" && (
            <WolfBetGrid
              betPerPlayer={Number(optionValues.betPerPlayer) || 0}
              blindEnabled={optionValues.blindEnabled !== false}
              blindMultiplier={String(optionValues.blindMultiplier ?? "2")}
              multiplierChoices={
                game.options.find((o) => o.key === "blindMultiplier")
                  ?.choices ?? []
              }
              onChange={onOptionChange}
            />
          )}
          {/* Bet-mode grid — three columns that stay anchored across
              Nassau states. Owns all five bet inputs:
                • Col 1 = Bet per Point ↔ Front 9 bet
                • Col 2 = (empty when Nassau off) ↔ Back 9 bet
                • Col 3 = Bet per Player ↔ Overall bet
              Only titles + radios change on Nassau toggle; the inputs
              stay in place (overallBet always sits in col 3). The
              five keys are filtered out of the default loop below to
              avoid doubling. Applies to every game in HAS_BET_MODE_GRID. */}
          {HAS_BET_MODE_GRID.has(game.id) &&
            (() => {
              // Only render the grid when overallBet is actually
              // visible for this game's current state — Hollywood
              // hides the grid in Vegas mode (no per-player /
              // per-point trio; just a flat pointBet input).
              const overallOpt = game.options.find(
                (o) => o.key === "overallBet",
              );
              if (!overallOpt) return null;
              if (!isOptionVisible(overallOpt, optionValues)) return null;
              return (
                <StablefordBetGrid
                  nassau={optionValues.nassau === true}
                  overallBet={Number(optionValues.overallBet) || 0}
                  betPerPoint={Number(optionValues.betPerPoint) || 0}
                  frontNineBet={Number(optionValues.frontNineBet) || 0}
                  backNineBet={Number(optionValues.backNineBet) || 0}
                  useBetPerPoint={optionValues.useBetPerPoint === true}
                  onChange={onOptionChange}
                />
              );
            })()}
          {/* Hollywood shotgun-start row — checkbox + hole selector inline. */}
          {game.id === "hollywood" && (
            <HollywoodStartHoleRow
              changeStartHole={optionValues.changeStartHole === true}
              startHole={Number(optionValues.startHole) || 1}
              onChange={onOptionChange}
            />
          )}
          {advancedOpts
            .filter(
              (opt) =>
                !(
                  HAS_BET_MODE_GRID.has(game.id) &&
                  BET_GRID_KEYS.has(opt.key)
                ) &&
                !(game.id === "wolf" && WOLF_GRID_KEYS.has(opt.key)) &&
                !(
                  game.id === "hollywood" &&
                  HOLLYWOOD_START_KEYS.has(opt.key)
                ),
            )
            .map((opt) => (
            <GameOptionField
              key={opt.key}
              option={opt}
              label={resolveOptionLabel(game.id, opt, optionValues)}
              value={optionValues[opt.key]}
              onChange={(v) => onOptionChange(opt.key, v)}
            />
          ))}
        </div>
      )}
    </li>
  );
}

// ─── Single option control ─────────────────────────────────────────────────

function GameOptionField({
  option,
  label,
  value,
  onChange,
}: {
  option: GameOption;
  // Caller-resolved label override. Most options just pass through
  // `option.label`; a few (e.g. Stableford's overallBet) flip their
  // label based on sibling option values, which the parent resolves
  // via resolveOptionLabel.
  label?: string;
  value: boolean | number | string | undefined;
  onChange: (next: boolean | number | string) => void;
}) {
  const v = value ?? option.default;
  const displayLabel = label ?? option.label;

  if (option.type === "boolean") {
    return (
      <label className="cg-games-opt cg-games-opt-bool">
        <input
          type="checkbox"
          checked={!!v}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span>{displayLabel}</span>
      </label>
    );
  }

  if (option.type === "select") {
    // Opt-in render override — `renderAs: "radio"` swaps the dropdown
    // for side-by-side radio buttons with no header. Used by binary
    // toggles (handicap mode, Skins pot vs carry, Aces & Deuces
    // net/gross, …) where seeing both values at once reads more
    // naturally than a hidden dropdown.
    if (option.renderAs === "radio") {
      return (
        <div className="cg-games-opt cg-games-opt-handicap-radio">
          {(option.choices ?? []).map((c) => (
            <label key={c.value} className="cg-handicap-radio-option">
              <input
                type="radio"
                checked={String(v) === c.value}
                onChange={() => onChange(c.value)}
              />
              <span>{c.label}</span>
            </label>
          ))}
        </div>
      );
    }
    // Rule-style choices read more naturally as a dropdown than a wheel.
    // Wheels are reserved for numeric ranges where snapping through values
    // is the natural gesture; named options aren't.
    return (
      <label className="cg-games-opt cg-games-opt-select">
        <span className="cg-games-opt-label">{displayLabel}</span>
        <select
          className="cg-games-opt-dropdown"
          value={String(v)}
          onChange={(e) => onChange(e.target.value)}
        >
          {(option.choices ?? []).map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </select>
      </label>
    );
  }

  // number — compact "[ value ]" trigger that opens a magnified scroll
  // popover on tap. Same gesture as the per-hole score entry, kept tiny
  // so several bet inputs can sit side-by-side without dominating the
  // game row. Min/max/step come from the catalog; uncapped options fall
  // back to NUMBER_DEFAULT_MAX so the popover always has a finite range.
  const min = option.min ?? 0;
  const max = option.max ?? NUMBER_DEFAULT_MAX;
  const step = option.step ?? 1;
  const numeric = typeof value === "number" ? value : Number(value) || 0;

  return (
    <label className="cg-games-opt cg-games-opt-num">
      <span className="cg-games-opt-label">{displayLabel}</span>
      <NumericPopoverPicker
        value={numeric}
        onChange={(next) => onChange(next)}
        min={min}
        max={max}
        step={step}
        values={option.values}
        zeroLabel={option.zeroLabel}
        ariaLabel={option.label}
      />
    </label>
  );
}

// ─── Hollywood shotgun-start row (custom inline layout) ────────────────────
//
// One line: a checkbox + "Change starting hole" text, with the 1–18 hole
// selector sitting inline right after the text once it's ticked. Replaces
// the two stacked option fields so the picker reads as part of the toggle.
function HollywoodStartHoleRow({
  changeStartHole,
  startHole,
  onChange,
}: {
  changeStartHole: boolean;
  startHole: number;
  onChange: (key: string, value: boolean | number | string) => void;
}) {
  return (
    <div className="cg-games-opt cg-hollywood-start-row">
      <label className="cg-hollywood-start-check">
        <input
          type="checkbox"
          checked={changeStartHole}
          onChange={(e) => onChange("changeStartHole", e.target.checked)}
        />
        <span>Change starting hole</span>
      </label>
      {changeStartHole && (
        <NumericPopoverPicker
          value={startHole || 1}
          onChange={(next) => onChange("startHole", next)}
          min={1}
          max={18}
          values={HOLE_VALUES}
          ariaLabel="Starting hole"
        />
      )}
    </div>
  );
}

// ─── Wolf bet grid (custom layout) ─────────────────────────────────────────
//
// Bet per Player and the Blind Wolf Multiplier sit side by side. The right
// title wraps to two lines (BLIND WOLF / MULTIPLIER) so it fits; the row is
// bottom-aligned so the two input boxes line up — which puts the "Bet per
// Player" line level with the "Multiplier" line. The enable checkbox sits
// between the two boxes, nudged toward the multiplier box, and greys the
// dropdown out when off.
//
//   BET PER PLAYER                 BLIND WOLF
//                                  MULTIPLIER
//   [  1  ]              ☑   [  2×  ]
function WolfBetGrid({
  betPerPlayer,
  blindEnabled,
  blindMultiplier,
  multiplierChoices,
  onChange,
}: {
  betPerPlayer: number;
  blindEnabled: boolean;
  blindMultiplier: string;
  multiplierChoices: Array<{ value: string; label: string }>;
  onChange: (key: string, value: boolean | number | string) => void;
}) {
  return (
    <div className="cg-wolf-grid">
      <div className="cg-wolf-grid-col">
        <span className="cg-wolf-grid-title">Bet per Player</span>
        <NumericPopoverPicker
          value={betPerPlayer}
          onChange={(next) => onChange("betPerPlayer", next)}
          min={0}
          max={NUMBER_DEFAULT_MAX}
          step={1}
          zeroLabel="—"
          ariaLabel="Bet per player"
        />
      </div>

      <span className="cg-wolf-grid-spacer" aria-hidden />

      <label
        className="cg-wolf-grid-check"
        title="Enable the Blind Wolf multiplier"
      >
        <input
          type="checkbox"
          checked={blindEnabled}
          onChange={(e) => onChange("blindEnabled", e.target.checked)}
          aria-label="Enable Blind Wolf multiplier"
        />
      </label>

      <div className="cg-wolf-grid-col">
        <span className="cg-wolf-grid-title">
          Blind Wolf
          <br />
          Multiplier
        </span>
        <select
          className="cg-games-opt-dropdown cg-wolf-grid-select"
          value={blindMultiplier}
          disabled={!blindEnabled}
          onChange={(e) => onChange("blindMultiplier", e.target.value)}
          aria-label="Blind Wolf multiplier"
        >
          {multiplierChoices.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

// ─── Stableford bet grid (custom layout) ──────────────────────────────────
//
// Three-column block that owns every Stableford bet input. Slot
// positions stay fixed across Nassau states so flipping the toggle
// only swaps titles + adds the middle Front 9 / Back 9 boxes; the
// rightmost column always carries overallBet so the input never jumps:
//
//   Nassau OFF:
//     Bet per Point    │  (empty)         │  Bet per Player
//     ( ) [—]          │                  │  ( ) [5]
//
//   Nassau ON:
//     Front 9 bet      │  Back 9 bet      │  Overall bet
//     [5]              │  [5]             │  [5]
//
// When Nassau is off the two outer cells get radios — the inactive
// one's input dims to 40% and disables pointer interaction. Clicking
// anywhere in a cell still bubbles to the wrapping <label> and flips
// the radio, which immediately re-enables the input. When Nassau is
// on the radios disappear and all three inputs are plain (pot-style).
//
// State stays in five flat catalog options (overallBet, betPerPoint,
// useBetPerPoint, frontNineBet, backNineBet) so the runner reads them
// unchanged and history rounds stay compatible — this component is
// purely a presentation rewrite for the bet block.
function StablefordBetGrid({
  nassau,
  overallBet,
  betPerPoint,
  frontNineBet,
  backNineBet,
  useBetPerPoint,
  onChange,
}: {
  nassau: boolean;
  overallBet: number;
  betPerPoint: number;
  frontNineBet: number;
  backNineBet: number;
  useBetPerPoint: boolean;
  onChange: (key: string, next: boolean | number | string) => void;
}) {
  if (nassau) {
    return (
      <div className="cg-stableford-bet-grid">
        <div className="cg-bet-grid-cell">
          <span className="cg-bet-grid-title">Front 9 bet</span>
          <span className="cg-bet-grid-controls">
            <NumericPopoverPicker
              value={frontNineBet}
              onChange={(v) => onChange("frontNineBet", v)}
              min={0}
              max={100}
              step={1}
              zeroLabel="—"
              ariaLabel="Front 9 bet"
            />
          </span>
        </div>
        <div className="cg-bet-grid-cell">
          <span className="cg-bet-grid-title">Back 9 bet</span>
          <span className="cg-bet-grid-controls">
            <NumericPopoverPicker
              value={backNineBet}
              onChange={(v) => onChange("backNineBet", v)}
              min={0}
              max={100}
              step={1}
              zeroLabel="—"
              ariaLabel="Back 9 bet"
            />
          </span>
        </div>
        <div className="cg-bet-grid-cell">
          <span className="cg-bet-grid-title">Overall bet</span>
          <span className="cg-bet-grid-controls">
            <NumericPopoverPicker
              value={overallBet}
              onChange={(v) => onChange("overallBet", v)}
              min={0}
              max={100}
              step={1}
              zeroLabel="—"
              ariaLabel="Overall bet"
            />
          </span>
        </div>
      </div>
    );
  }
  return (
    <div className="cg-stableford-bet-grid">
      <label className="cg-bet-grid-cell">
        <span className="cg-bet-grid-title">Bet per Point</span>
        <span className="cg-bet-grid-controls">
          <input
            type="radio"
            name="stableford-bet-mode"
            checked={useBetPerPoint}
            onChange={() => onChange("useBetPerPoint", true)}
          />
          <span
            className={`cg-bet-input-wrap${
              !useBetPerPoint ? " cg-bet-input-disabled" : ""
            }`}
          >
            <NumericPopoverPicker
              value={betPerPoint}
              onChange={(v) => onChange("betPerPoint", v)}
              min={0}
              max={100}
              step={1}
              zeroLabel="—"
              ariaLabel="Bet per Point"
            />
          </span>
        </span>
      </label>
      {/* Middle slot stays empty when Nassau is off — reserves the
          column so the Bet-per-Player input lands in the same screen
          position as Overall will when Nassau ticks on. */}
      <div className="cg-bet-grid-cell cg-bet-grid-cell-spacer" aria-hidden />
      <label className="cg-bet-grid-cell">
        <span className="cg-bet-grid-title">Bet per Player</span>
        <span className="cg-bet-grid-controls">
          <input
            type="radio"
            name="stableford-bet-mode"
            checked={!useBetPerPoint}
            onChange={() => onChange("useBetPerPoint", false)}
          />
          <span
            className={`cg-bet-input-wrap${
              useBetPerPoint ? " cg-bet-input-disabled" : ""
            }`}
          >
            <NumericPopoverPicker
              value={overallBet}
              onChange={(v) => onChange("overallBet", v)}
              min={0}
              max={100}
              step={1}
              zeroLabel="—"
              ariaLabel="Bet per Player"
            />
          </span>
        </span>
      </label>
    </div>
  );
}
